
import { ChangeDetectionStrategy, Component, computed, inject, signal, } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { TranslateModule } from '@ngx-translate/core';

import {
  MethodContextEnum,
  ModalResult,
  SocialNetworksEnum,
  UnavailableMethodData,
} from '@personal-area/global/social-authorization/models';
import { IconComponent } from '@personal-area/global/ui';

@Component({
    selector: 'global-unavailable-method-modal',
    imports: [TranslateModule, IconComponent],
    templateUrl: './unavailable-method-modal.component.html',
    styleUrl: './unavailable-method-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.login-popup]': 'isLoginContext',
    }
})
export class UnavailableMethodModalComponent {
  private readonly _dialogRef = inject(
    MatDialogRef<UnavailableMethodModalComponent>,
  );
  private readonly _data = inject<UnavailableMethodData>(MAT_DIALOG_DATA);

  public readonly emailAvailable = true;
  public readonly socialMethod = this._data.method.name;
  public readonly socialMethods = this._data.availableMethods;
  public readonly isLoginContext = this._data.context === MethodContextEnum.Login;

  public isHovered = signal<boolean>(false);

  public readonly getIconSrc = computed(() =>
    this.isHovered()
      ? './assets/img/icons/social/i-email-white.svg'
      : './assets/img/icons/social/i-email.svg',
  );

  public readonly descriptionKey = (() => {
    const context = this.isLoginContext ? 'sign-in' : 'sign-up';
    const isApple = this._data.method.id === SocialNetworksEnum.Apple;

    if (isApple && this.isLoginContext) {
      return `unavailable-modal.method.description-apple.${context}`;
    }

    if (!this.isLoginContext) {
      const totalAvailableMethods =
        this.socialMethods.length + (this.emailAvailable ? 1 : 0);

      if (totalAvailableMethods === 1) {
        return this.emailAvailable
          ? `unavailable-modal.method.description-email.${context}`
          : `unavailable-modal.method.description-single.${context}`;
      }
    }

    return `unavailable-modal.method.description.${context}`;
  })();

  public readonly descriptionParams = (() => {
    if (!this.isLoginContext) {
      const totalAvailableMethods =
        this.socialMethods.length + (this.emailAvailable ? 1 : 0);

      if (totalAvailableMethods === 1 && !this.emailAvailable) {
        return { method: this.socialMethods[0].name };
      }
    }
    return {};
  })();

  public close(): void {
    this._dialogRef.close();
  }

  public onSocialMethodClick(method: { id: SocialNetworksEnum }): void {
    const result: ModalResult = {
      selectedMethod: method.id,
    };
    this._dialogRef.close(result);
  }

  public onEmailClick(): void {
    const result: ModalResult = { selectedMethod: 'email' as const };
    this._dialogRef.close(result);
  }

  public onResetPassword(): void {
    const result: ModalResult = {
      action: 'resetPassword',
      sourceMethod: this._data.method.id,
    };
    this._dialogRef.close(result);
  }

  public onContactSupport(): void {
    const result: ModalResult = { action: 'contactSupport' };
    this._dialogRef.close(result);
  }
}
